import React, { forwardRef, useContext, useEffect, useImperativeHandle, useRef, useState } from "react";
import { AuthContext } from "../../../../../provider/authProvider";
import { IbanElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { getAPIAxios, getAxios } from "../../../../../helper/constants/uri";
import ShLoadingIndicatorModalLazy from "../../../../../components/loading-indicator/shLoadingIndicatorModalLazy";
import ShUserMessage from "../../../../../components/user-message/user-message";

import styles from "./shCheckoutStripe.module.scss";
import useAuthState from "../../../../../zustand/authState";
import { checkStripeUpdate } from "../../helper/stripe/shStripeInfoChecker";
import { OwnProfileService } from "../../../../../generated";

const ShCheckoutStripeSEPA = (
    {
        url = null,
        postData = { },

        paymentSucceeded = (params: { transactionId: string, transactionDate: string} ) => { }
    },
    ref
) => {

    const authContext = useContext(AuthContext);
    const currentUserOverview = useAuthState(s => s.currentUserOverview);

    const firstnameInputRef = useRef(null);
    const lastnameInputRef = useRef(null);

    const [ errorMessage, setErrorMessage ] = useState(null);
    const [ processing, setProcessing ] = useState(false);

    const stripe = useStripe();
    const elements = useElements();


    useEffect(
        () => {
            if (currentUserOverview) {
                if (firstnameInputRef.current) {
                    firstnameInputRef.current.value = currentUserOverview.firstname;
                }

                if (lastnameInputRef.current) {
                    lastnameInputRef.current.value = currentUserOverview.lastname;
                }
            }
        },
        [ currentUserOverview]
    )

    const handleChange = async (event) => {
        // Listen for changes in the CardElement and display any errors as the customer types their card details
        setErrorMessage(event.error ? event.error.message : "");
    };

    const handleSubmit = async () => {
        // already working on this
        if (processing) {
            return;
        }

        setProcessing(true);

        const firstname = firstnameInputRef.current ? firstnameInputRef.current.value : undefined;
        const lastname = lastnameInputRef.current ? lastnameInputRef.current.value : undefined;

        // update own profile with first/lastname
        try {
            await OwnProfileService.updateSepaProfile({firstname, lastname});
        } catch(err) {
            // there shouldn't be a stop anyway
            console.log('*** error updating firstname/lastname of user', err);
        }


        const axios = getAPIAxios();
        let payload = undefined;

        // perform standard transaction
        try {
            const res = await axios.post(url, postData);
            const clientSecret = res.data.clientSecret;
            const setupIntentClientSecret = res.data.setupIntentClientSecret;

            if (clientSecret) {
                payload = await stripe.confirmSepaDebitPayment(clientSecret, {
                    payment_method: {
                        sepa_debit: elements.getElement(IbanElement),
                        billing_details: {
                            email: authContext.currentUserOverview?.current?.mail,
                            name: firstname + " " + lastname
                        }
                    }
                });
            }

            if (setupIntentClientSecret) {
                payload = await stripe.confirmSepaDebitSetup(
                    setupIntentClientSecret, {
                        payment_method: {
                            sepa_debit: elements.getElement(IbanElement),
                            billing_details: {
                                email: authContext.currentUserOverview?.current?.mail,
                                name: firstname + " " + lastname
                            }
                        }
                    }
                );
            }

            if (payload.error) {
                setErrorMessage(payload.error.message);
                await authContext.updateOwnOverview();
            } else {
                const stripeInfo = await checkStripeUpdate();

                if (!stripeInfo) {
                    setErrorMessage("Eventuell dauert es noch einen Moment bis die Zahlung bei uns angekommen ist.");
                    await paymentSucceeded(null);
                } else {
                    await paymentSucceeded(stripeInfo);
                }
            }

        } catch(err) {
            console.log(err);
            setErrorMessage("Unbekannter Fehler passiert");
            await authContext.updateOwnOverview();
        } finally {
            setProcessing(false);
        }

    };

    useImperativeHandle(ref, () => ({
        processPayment: () => {
            handleSubmit();
        }
    }));

    const IBAN_STYLE = {
        base: {
            iconColor: '#2a3349',
            color: '#2a3349',
            fontSize: '16px',
            '::placeholder': {
                color: '#9398a3'
            },
            ':-webkit-autofill': {
                color: '#2a3349',
            },
        },
        invalid: {
            color: '#f30000',
            iconColor: '#f30000',
            ':-webkit-autofill': {
                color: '#f30000',
            },
        }
    };

    const IBAN_ELEMENT_OPTIONS = {
        supportedCountries: ['SEPA'],
        // Elements can use a placeholder as an example IBAN that reflects
        // the IBAN format of your customer's country. If you know your
        // customer's country, we recommend that you pass it to the Element as the
        // placeholderCountry.
        placeholderCountry: authContext.currentUserOverview.current.country ? authContext.currentUserOverview.current.country : 'DE',
        style: IBAN_STYLE,
    };


    return <>
        <ShLoadingIndicatorModalLazy isOpen={processing} />
        <form className={styles.form} id="iban-payment-form" onSubmit={handleSubmit}>

            <div className="row">
                <div className="col-6">
                    <div className="form-control">
                        <input ref={firstnameInputRef} type={"text"} placeholder="Vorname" />
                    </div>
                </div>

                <div className="col-6">
                    <div className="form-control">
                        <input ref={lastnameInputRef} type={"text"} placeholder="Nachname" />
                    </div>
                </div>
            </div>

            <div className={styles.wrapper + " mt-3"}>
                <IbanElement options={IBAN_ELEMENT_OPTIONS} id={"iban-element"} onChange={handleChange} />
            </div>

            {errorMessage &&
                <ShUserMessage error={true} message={errorMessage} className="mt-2 ml-3"/>
            }

        </form>
    </>

};

export default forwardRef(ShCheckoutStripeSEPA);
